import { useCallback, useMemo, useState } from 'react';

import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';

export type ConditionalStep<T> = {
  name: T;
  visible?: boolean;
};

type Step<T> = Omit<ConditionalStep<T>, 'visible'> & {
  key: T;
  index: string;
};

/**
 * Hook for managing steps with dynamic visibility / length
 */
export const useConditionalSteps = <T = unknown>(
  steps: ConditionalStep<T>[],
  isPayout: boolean = true
) => {
  const dispatchGAEvent = useGoogleAnalyticsDispatcher();

  /**
   * Private
   */
  const _isVisible = useCallback(
    (step: ConditionalStep<T>) => step.visible === undefined || step.visible,
    []
  );

  const visibleSteps: Step<T>[] = useMemo(
    () =>
      steps
        .filter(_isVisible)
        .map(({ name }, index) => ({ name, key: name, index: `${index + 1}` })),
    [steps]
  );

  const [completedSteps, setCompletedSteps] = useState<T[]>([]);

  /**
   * Public
   */
  const [currentStep, setStep] = useState<T>(visibleSteps[0]?.name);

  const isVisible = useCallback(
    (name: T) => _isVisible(steps.find((step) => step.name === name)!),
    [steps]
  );

  const isFinished = useCallback(
    (name: T) => completedSteps.includes(name),
    [completedSteps]
  );

  const getStep = useCallback(
    (name: T) => {
      const { key: _key, ...step } = visibleSteps.find((s) => s.name === name)!;
      return { ...step, isFinished: isFinished(name) };
    },
    [visibleSteps, isFinished]
  );

  const completeStep = useCallback(() => {
    const currentIndex = visibleSteps.findIndex(
      (step) => step.name === currentStep
    );

    setCompletedSteps((_steps) => [..._steps, currentStep]);

    const nextStep = visibleSteps[currentIndex + 1]?.name;
    setStep(() => nextStep);
    if (isPayout) {
      dispatchGAEvent('create_manual_payout_proceeded', {
        from: currentStep,
        to: nextStep,
        version: 'v1'
      });
    }
  }, [visibleSteps, currentStep]);

  const lastStep = useMemo(
    () => visibleSteps[visibleSteps.length - 1]?.name,
    [visibleSteps]
  );

  return {
    currentStep,
    setStep,
    getStep,
    completeStep,
    isVisible,
    isFinished,
    lastStep
  };
};
